import React from 'react';
import { ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line } from 'recharts';
import { TrendingUp, Sparkles, DollarSign } from 'lucide-react';
import { ChartData } from '../../types';
import { COLORS } from '../../constants';

interface DashboardRevenueChartProps {
  chartData: ChartData[];
  showForecast: boolean;
  currencySymbol: string;
  targetArr?: number;
  momGrowth?: number;
  grossMargin?: number;
}

const DashboardRevenueChart: React.FC<DashboardRevenueChartProps> = ({
  chartData,
  showForecast,
  currencySymbol,
  targetArr = 520000,
  momGrowth = 8.4,
  grossMargin = 87.2,
}) => {
  return (
    <div className="bg-white dark:bg-slate-800 p-5 md:p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xs flex flex-col justify-between">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-slate-900 dark:text-white">
              Revenue Trajectory & Forecast
            </h2>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
              <Sparkles className="w-3 h-3" /> AI Model
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Historical billing performance paired with predictive forward run-rate.
          </p>
        </div>

        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-indigo-600"></span>
            <span className="text-slate-600 dark:text-slate-300 font-medium">Billed MRR</span>
          </div>
          {showForecast && (
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-1 border-t-2 border-dashed border-indigo-400"></span>
              <span className="text-indigo-600 dark:text-indigo-400 font-medium">Projected</span>
            </div>
          )}
        </div>
      </div>

      <div className="h-[320px] w-full min-h-[320px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.2} />
                <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#94a3b8" opacity={0.15} />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#64748b', fontWeight: 600 }}
              dy={10}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickFormatter={(v) => `${currencySymbol}${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
            />
            <Tooltip
              contentStyle={{
                borderRadius: '12px',
                border: '1px solid #334155',
                backgroundColor: '#0f172a',
                padding: '12px 16px',
                color: '#fff',
                fontSize: '12px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)',
              }}
              formatter={(value: any, name: any) => [
                `${currencySymbol}${Number(value).toLocaleString()}`,
                name === 'revenue' ? 'Actual Revenue' : 'Forecast Projection',
              ]}
              labelStyle={{ color: '#94a3b8', fontWeight: 700, marginBottom: '4px' }}
            />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke={COLORS.primary}
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorRev)"
              connectNulls
            />
            {showForecast && (
              <Line
                type="monotone"
                dataKey="forecast"
                stroke="#818cf8"
                strokeWidth={2.5}
                strokeDasharray="6 4"
                dot={{ r: 3.5, fill: '#6366f1', strokeWidth: 2, stroke: '#fff' }}
                connectNulls
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700/60 grid grid-cols-3 gap-2 text-center">
        <div className="p-2 bg-slate-50 dark:bg-slate-700/30 rounded-lg">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Target ARR</p>
          <p className="text-sm font-extrabold text-slate-900 dark:text-white mt-0.5">
            {currencySymbol}{targetArr.toLocaleString()}
          </p>
        </div>
        <div className="p-2 bg-slate-50 dark:bg-slate-700/30 rounded-lg">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">MoM Growth</p>
          <p className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5">
            {momGrowth >= 0 ? `+${momGrowth}%` : `${momGrowth}%`}
          </p>
        </div>
        <div className="p-2 bg-slate-50 dark:bg-slate-700/30 rounded-lg">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Gross Margin</p>
          <p className="text-sm font-extrabold text-indigo-600 dark:text-indigo-400 mt-0.5">
            {grossMargin}%
          </p>
        </div>
      </div>
    </div>
  );
};

export default DashboardRevenueChart;
